import { type ReactNode, useEffect, useState } from "react";
import { useAdminApp } from "@/hooks/useAdminApp";
import { useStateMachineValue } from "@/core/arch/state_machine";
import { Button } from "@/components/ui/base/button";
import { Loader2 } from "lucide-react";
import { StepTimeline } from "../components/StepTimeline";
import type {
  AddressData,
  ProvisioningRequest,
} from "@/application/provisioning/types";

const FLAG: Record<string, string> = { CA: "Canada", US: "United States" };
const BIZ_TYPE: Record<string, string> = {
  individual: "Individual",
  company: "Company",
  non_profit: "Non-profit",
};

function formatAddr(a?: Partial<AddressData>): string {
  if (!a) return "—";
  return [a.line1, a.line2, a.city, a.state, a.postalCode]
    .filter(Boolean)
    .join(", ");
}

export function ReviewStep() {
  const { provisioningBloc, provisioningRepo } = useAdminApp();
  const [state] = useStateMachineValue(provisioningBloc);
  const { formData, submitting, result } = state;
  const [provisionRecord, setProvisionRecord] =
    useState<ProvisioningRequest | null>(null);

  // After submission completes, load the full provisioning record to show step timeline
  useEffect(() => {
    if (!result?.provisionId) {
      setProvisionRecord(null);
      return;
    }

    let cancelled = false;
    const loadRecord = async () => {
      try {
        const record = await provisioningRepo.get(result.provisionId as string);
        if (!cancelled) setProvisionRecord(record);
      } catch {
        // Silently ignore — the result banner already shows status
      }
    };

    loadRecord();
    return () => {
      cancelled = true;
    };
  }, [result?.provisionId, provisioningRepo]);

  return (
    <div>
      <h2 className="text-foreground mb-6 text-lg font-semibold">
        Review & Create
      </h2>

      <Section title="Contact">
        <Row
          label="Name"
          value={`${formData.firstName} ${formData.lastName}`}
        />
        <Row label="Email" value={formData.email} />
        <Row label="Phone" value={formData.phone} />
      </Section>

      <Section title="Business">
        <Row label="Business Name" value={formData.businessName} />
        <Row label="Country" value={FLAG[formData.businessCountry ?? "CA"]} />
        <Row
          label="Type"
          value={BIZ_TYPE[formData.businessType ?? "individual"]}
        />
        <Row label="Category Code" value={formData.businessCategoryCode} />
        <Row label="Date of Birth" value={formData.birthDate} />
        {formData.taxId && <Row label="Tax ID (BN)" value={formData.taxId} />}
        {formData.ssnLast4 && (
          <Row label="SSN Last 4" value={`****${formData.ssnLast4}`} />
        )}
        {formData.businessUrl && (
          <Row label="Business URL" value={formData.businessUrl} />
        )}
      </Section>

      <Section title="Addresses">
        <Row label="Business" value={formatAddr(formData.businessAddress)} />
        <Row
          label="Individual"
          value={formatAddr(formData.individualAddress)}
        />
      </Section>

      {result?.errors && result.errors.length > 0 && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="text-sm font-semibold text-red-700">
            Provisioning failed
          </div>
          {result.errors.map((err, i) => (
            <div key={i} className="mt-1 text-sm text-red-600">
              {err}
            </div>
          ))}
          {result.failedStep && (
            <div className="text-muted-foreground mt-1 text-xs">
              Failed at step: {result.failedStep}
            </div>
          )}
        </div>
      )}

      {result?.status === "completed" && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 text-sm font-medium text-green-700">
          Account provisioned successfully!
        </div>
      )}

      {provisionRecord && (
        <div className="bg-card mb-4 rounded-lg border p-4">
          <h3 className="text-foreground mb-3 text-sm font-semibold">
            Provisioning Steps
          </h3>
          <StepTimeline
            currentStep={provisionRecord.currentStep}
            failedStep={provisionRecord.failedStep}
            stepHistory={provisionRecord.stepHistory}
            status={provisionRecord.status}
          />
        </div>
      )}

      <div className="mt-6 flex justify-between">
        <Button
          variant="outline"
          onClick={() => provisioningBloc.prevStep()}
          disabled={submitting}
        >
          ← Back
        </Button>
        <Button
          onClick={() => provisioningBloc.submit()}
          disabled={submitting || result?.status === "completed"}
        >
          <SubmitButtonContent
            submitting={submitting}
            failed={result?.status === "failed"}
          />
        </Button>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="bg-card mb-6 rounded-lg border p-4">
      <h3 className="text-foreground mb-3 text-sm font-semibold">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function SubmitButtonContent({
  submitting,
  failed,
}: {
  submitting: boolean;
  failed: boolean;
}) {
  if (submitting) {
    return (
      <>
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Creating Account...
      </>
    );
  }
  if (failed) return <>Retry</>;
  return <>Create Account</>;
}

function Row({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground">{value || "—"}</span>
    </div>
  );
}
